import React, { useState, useEffect } from 'react';
import { useStore } from 'react-redux';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';

import SearchBox from 'components/SearchBox';
import ZeroStateView from 'components/ZeroStateView';

import RuleSummaryRow from './RuleSummaryRow';
import { SourceHeading, TargetHeading } from './reviewConditions/Headings';

import { getFilteredRules } from 'app-components/flux/transaction-rules/transactionRulesSelectors';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { tracker } from 'app-components/utils/core';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  background: {
    background: theme.palette.background.default,
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: '0 !important',
  },
  containerRow: {
    display: 'flex',
    '&:not(:last-of-type)': {
      borderBottom: `1px ${theme.palette.divider} solid`,
    },
    '>*': {
      display: 'flex',
      flex: 1,
      '&:not(:last-of-type)': {
        borderRight: `1px ${theme.palette.divider} solid`,
      },
    },
  },
  row: {
    display: 'flex',
    flex: 1,
    background: theme.palette.background.default,
  },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    gap: 8,
    '& .MuiCardHeader-action': {
      margin: 0,
    },
  },
  ruleListActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  zeroState: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flex: 0,
    alignItems: 'center',
    gap: theme.spacing(2),
    height: '50vh',
  },
}));

type RulesListProps = {
  rules: TransactionRuleType[];
  // onOpenEditorCallback: () => void;
  onEditCallback: (id?: string) => void;
  onDeleteCallback?: (id: string) => void;
  onDuplicateCallback?: (id: string) => void;
};

const RulesList: React.FC<RulesListProps> = ({
  rules,
  onEditCallback,
  onDeleteCallback,
  // onDuplicateCallback,
}) => {
  const { cx, classes } = useStyles();
  const [filteredRules, setFilteredRules] = useState<TransactionRuleType[]>(rules);
  const [searchValue, setSearchValue] = useState<string>();
  const store = useStore();

  useEffect(() => {
    if (searchValue) {
      const filtered = getFilteredRules(store.getState(), searchValue);
      setFilteredRules(filtered);
      tracker.track(tracker.events.txnRuleSearch);
    } else {
      setFilteredRules(rules);
    }
  }, [rules, searchValue, store]);

  return (
    <Card id='rules-list-container'>
      <CardHeader
        id='rules-list-count'
        className={classes.cardHeader}
        title={
          filteredRules.length < rules.length
            ? `${filteredRules.length} of ${rules.length} Rules`
            : `${rules.length} Rules`
        }
        titleTypographyProps={{ variant: 'titleMedium' }}
        action={
          <Box className={classes.ruleListActions}>
            <SearchBox
              id='rules-search-box'
              placeholder='Search rules'
              onSearch={setSearchValue}
              initialValue={searchValue}
              autoSearch
              autoFocus
            />
          </Box>
        }
      />

      {rules.length === 0 ? (
        <CardContent className={classes.zeroState} id='rules-list-zero-state'>
          <ZeroStateView
            size='small'
            animate
            id='rules-list-zero-state'
            title='Rules you create will appear here.'
            qIllustration='rules'
            button='Create Rule'
            buttonId='rule-create-button'
            onClick={() => onEditCallback()}
          />
        </CardContent>
      ) : (
        <CardContent className={classes.container} id='rules-list'>
          <Box className={cx(classes.containerRow, classes.row)}>
            <SourceHeading className={classes.background} />
            <TargetHeading className={classes.background} />
          </Box>
          {filteredRules &&
            filteredRules.map((rule) => (
              <Box className={classes.containerRow} key={rule.id || 0}>
                <RuleSummaryRow
                  rule={rule}
                  onEditCallback={onEditCallback}
                  onDeleteCallback={onDeleteCallback}
                  // onDuplicateCallback={onDuplicateCallback}
                />
              </Box>
            ))}
        </CardContent>
      )}
    </Card>
  );
};

export default RulesList;
